import React, { useState } from 'react';
import styled from 'styled-components/native';

import { WhereClause } from '@people-science/query-core';

import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../../components/AlertBox';
import GradientProgressBar from '../../../../components/GradientProgressBar';
import { B, H5, P } from '../../../../components/Typography';
import { useQueryStudyParticipantImpact } from '../../../../hooks/study-participant/useQueryStudyParticipantImpact';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import WebViewModal from '../../../modals/WebViewModal';
import { Octicons } from '@expo/vector-icons';
import IconButton from '../../../../components/IconButton';
import Card from '../../../../components/Card';
import Theme from '../../../../constants/Theme';

const StyledView = styled(Card)`
  position: relative;
  padding: 25px;
  width: 100%;
`;

const StyledCardHeaderView = styled.View`
  flex-grow: 1;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledTitleView = styled.View`
  flex: 1;
`;

const StyledTitleHeaderView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${2 * Theme.spacing}px;
`;

const StyledMeterContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StyledMeterText = styled(P)`
  font-style: italic;
`;

const StyledMeasurementContainer = styled.View`
  margin-bottom: 25px;
`;

const StyledMeasurementText = styled(B)`
  margin-top: 8px;
`;

export interface ImpactProgressMeasurement {
  label?: string;
  entityId?: string;
  columnName?: string;
  wheres?: WhereClause[];
  enableBaselineAverage?: boolean;
  baselineAverage?: number;
}
export interface ImpactProgressCardProps {
  title?: string;
  hasMoreInfo?: boolean;
  infoTitle?: string;
  infoHtml?: string;
  description?: string;
  measurements?: ImpactProgressMeasurement[];
}

export const ImpactProgressCard: React.FC<
  ScreenComponentProps<ImpactProgressCardProps>
> = ({
  title,
  hasMoreInfo,
  infoTitle,
  infoHtml,
  description,
  measurements = [],
  studyParticipant
}) => {
  const { impactResults, loadingImpact, loadingImpactError } =
    useQueryStudyParticipantImpact(studyParticipant?.id || 0, measurements);
  const [showModal, setShowModal] = useState<boolean>();
  return (
    <StyledView>
      <StyledCardHeaderView>
        <StyledTitleView>
          <StyledTitleHeaderView>
            <H5 color='dark'>{title || 'Results: Impact'}</H5>
            {hasMoreInfo && (
              <IconButton onPress={() => setShowModal(true)}>
                <Octicons name='question' size={24} color='#494753' />
              </IconButton>
            )}
          </StyledTitleHeaderView>
          {description && (
            <B color='dark' padding={3}>
              {description}
            </B>
          )}
        </StyledTitleView>
      </StyledCardHeaderView>

      <StyledMeterContainer>
        <StyledMeterText color='dark'>Unclear</StyledMeterText>
        <StyledMeterText color='dark'>Highly Likely</StyledMeterText>
      </StyledMeterContainer>
      {/* TODO: change the progress bar depending on study results */}
      {measurements?.map((item, index) => (
        <StyledMeasurementContainer key={index}>
          <GradientProgressBar
            progress={studyParticipant?.id ? impactResults[index]?.p || 0 : 0.3}
          />
          <StyledMeasurementText color='dark'>
            {item.label || `Measurement ${index + 1}`}
          </StyledMeasurementText>
        </StyledMeasurementContainer>
      ))}
      {loadingImpact && <ActivityIndicatorScreen modal />}
      {loadingImpactError && (
        <AlertBox fullWidth severity='error'>
          Chart error: {loadingImpactError.message}
        </AlertBox>
      )}
      {showModal && (
        <WebViewModal
          contentPadding
          visible={showModal}
          title={infoTitle || ''}
          html={infoHtml || ''}
          onClose={() => setShowModal(false)}
        />
      )}
    </StyledView>
  );
};

export const ImpactProgressCardPlugin: ScreenComponentPlugin<ImpactProgressCardProps> =
  {
    type: 'ImpactProgressCard',
    name: 'Impact Progress Card',
    categories: ['study-progress', 'my-chloe'],
    component: ImpactProgressCard
  };
